import SVM from "libsvm-js/asm";
import { MathType, Matrix, matrix } from "mathjs";
import {
  RandomForestClassifier,
  RandomForestRegression,
} from "ml-random-forest";
import { handleError } from "../App";
import {
  ALL_MODELS,
  CLASSIFICATION_MODELS,
  DATA_TYPES,
  MAX_ROWS_FAST,
  MAX_ROWS_SLOW,
  MODEL_STATES,
  REGRESSSION_MODELS,
  WORKER_TYPES,
} from "../constants";
import { sleep } from "../dashboard/Common/helpers";
import { setDefaultPersona } from "../dashboard/PersonaPrediction/PersonaPredictionBox";
import { convertModels } from "../helpers/convertModels";
import { stateTypeF } from "../state";
import {
  FAILED_STATUS,
  getLossWeights,
  SUCCESS_STATUS,
  WEIGHTED,
} from "./BackgroundTrainer";
import { BaseModel } from "./BaseModel";
import Column, { colStatsType } from "./Column";
import Dataset, { dataSplit, infoType } from "./Dataset";
import { getWorker } from "./getWorker";
import { kNearestNeighbours } from "./kNearestNeighbours";
import { LinearRegression } from "./LinearRegression";
import { LogisticRegression } from "./LogisticRegression";
import { NaiveBayes } from "./NaiveBayes";
import { NeuralNet } from "./NeuralNet";
import { makePredictions } from "./predictor";
import { RandomForest } from "./RandomForest";
import { linearModelType, RidgeRegression } from "./RidgeRegression";
import { SupportVectorMachine } from "./SupportVectorMachine";

const maxLossDiff = 0.1;

type returnedModelType = {
  model: any;
  lowestLoss: number;
  trainState: number;
  score: number;
};
let modelLowestLosses = {};

export async function postTrainProcess(
  state: stateTypeF,
  savedModels: any[],
  trainData: dataSplit,
  allColumns: Column[]
) {
  let yCol = state.saveState.dataset.getColumn(
    state.saveState.modelTraining.valueToPredict
  );
  const payload = {
    trainData: trainData,
    models: convertModels(savedModels),
    workerType: WORKER_TYPES.postTrainProcess,
    yCol: yCol,
    allColumns: allColumns,
  };
  const worker = getWorker();

  let resolves = {};
  let score = undefined;
  let outVals = undefined;
  let out = undefined;
  let importantVars = undefined;
  let varImpact = undefined;

  let workerStatus = FAILED_STATUS;
  worker.onmessage = function (message) {
    if (message.data?.status === SUCCESS_STATUS) {
      score = message.data.score;
      outVals = message.data.outVals;
      out = message.data.out;
      importantVars = message.data.importantVars;
      varImpact = message.data.varImpact;
      resolves[0]();
      delete resolves[0];

      workerStatus = SUCCESS_STATUS;
    } else {
      handleError(state, message.data?.error, WORKER_TYPES.postTrainProcess);
      resolves[0]();
      delete resolves[0];
    }
  };
  await new Promise(function (resolve, reject) {
    // save callbacks for later
    resolves[0] = resolve;
    worker.postMessage(payload);
  });
  if (workerStatus === FAILED_STATUS) {
    return false;
  }
  state.saveState.modelTraining.modelR2 = score;
  if (yCol.columnType === DATA_TYPES.categorical) {
    state.saveState.modelTraining.catModelPredictions = outVals;
  } else {
    state.saveState.modelTraining.modelPredictions = outVals;
  }
  state.saveState.variableImportance.varImpact = varImpact;
  state.saveState.variableImportance.varImportance = out;

  const impactVar = importantVars.filter((v) => v !== yCol.columnName)[0];
  state.saveState.variableImportance.impactVarSelected = impactVar;
  if (yCol.columnType === DATA_TYPES.categorical) {
    state.saveState.variableImportance.impactClassSelected =
      yCol.getClassByIndex(0);
  }
  state.setSaveState(state.saveState);
  return true;
}

export default async function startTrainFlow(state: stateTypeF) {
  state.setIsBusy(true);
  state.setIsProcessingData(true);
  let trainedModels = [];
  state.resetModelStates(state.saveState.modelTraining.modelStates);

  // Set model state variables
  modelLowestLosses = {};

  let col = state.saveState.dataset.getColumn(
    state.saveState.modelTraining.valueToPredict
  );

  for (const [_, value] of Object.entries(ALL_MODELS)) {
    BaseModel.setModelProgress(0, state, value);
    if (state.getCurrentModels(col.columnType).includes(value)) {
      modelLowestLosses[value] = 1e10;
    }
  }

  const dataset = state.saveState.dataset;
  const yColName = state.saveState.modelTraining.valueToPredict;
  const dataStr = JSON.stringify(state.saveState.dataset);
  const trainData = await processDataInBackground(
    dataStr,
    yColName,
    state.saveState.modelTraining.isFastTrainingSelected
      ? MAX_ROWS_FAST
      : MAX_ROWS_SLOW,
    state
  );
  if (trainData === undefined) {
    return;
  }
  dataset.clolumnsToDrop = trainData.colsToDrop;
  state.saveState.dataset = dataset;
  state.setSaveState(state.saveState);

  let test = trainData.xTest.slice(0, 1);
  let groundTruth = trainData.yTest.slice(0, 1);

  state.setIsProcessingData(false);
  state.setIsTraining(true);

  const start = performance.now();

  if (toTrain(state, ALL_MODELS.linReg)) {
    trainedModels.push(await trainLinearRegression(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.ridgeReg)) {
    trainedModels.push(await trainRidgeRegression(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.logistic)) {
    trainedModels.push(await trainLogisticRegression(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.bayes)) {
    trainedModels.push(await trainBayes(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.knn)) {
    trainedModels.push(await trainKNN(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.randomForest)) {
    trainedModels.push(await trainRandomForest(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.svm)) {
    trainedModels.push(await trainSVM(trainData, state));
  }
  if (toTrain(state, ALL_MODELS.neuralNet)) {
    trainedModels.push(await trainNeuralNet(trainData, state));
  }

  trainedModels = trainedModels.filter((v) => v !== undefined);
  console.log(state.getTrainingCanceled());

  if (state.getTrainingCanceled() || trainedModels.length === 0) {
    state.resetModelProgress();
    state.resetModelStates(state.saveState.modelTraining.modelStates);
    state.resetTotalTrainProgress();
    state.setIsBusy(false);
    state.setIsTraining(false);
    state.setTrainingCanceled(false);
    state.setIsAnalyzing(false);

    return;
  }

  // Test models
  trainedModels.map((model) =>
    testPrediction(
      model,
      dataset.getColumn(yColName).colStats,
      test,
      groundTruth,
      state
    )
  );

  // Save the best models to the state
  const chosenModels = chooseModels();
  let savedModels = [];
  for (let chosen of chosenModels) {
    for (let model of trainedModels) {
      if (model.modelName === chosen) {
        savedModels.push(model);
      }
    }
  }

  state.setIsTraining(false);
  state.setIsAnalyzing(true);
  await sleep(300);
  const success = await postTrainProcess(
    state,
    savedModels,
    trainData,
    dataset.columns
  );
  state.setIsBusy(false);
  state.setIsTraining(false);
  state.setIsSaved(false);
  state.setIsAnalyzing(false);
  state.setSaveStateModified(true);
  if (!success) {
    return;
  }

  let cols = dataset.columns.filter(
    (v) => v.columnType !== DATA_TYPES.unusable && v.columnName !== yColName
  );
  setDefaultPersona(state, cols, savedModels);

  state.setChosenModels(chosenModels);
  state.saveState.savedModels = savedModels;
  state.setSaveState(state.saveState);
}

function testPrediction(
  model:
    | NeuralNet
    | LinearRegression
    | RandomForest
    | kNearestNeighbours
    | SupportVectorMachine
    | RidgeRegression
    | LogisticRegression
    | NaiveBayes,
  yColStats: colStatsType,
  test: number[][],
  groundTruth: number[],
  state: stateTypeF
) {
  let output = makePredictions(
    test,
    state.saveState.dataset,
    state.saveState.modelTraining.valueToPredict,
    [model]
  );
  groundTruth = Dataset.deNormalize(groundTruth, yColStats);

  console.log(
    model.modelName +
      " output: " +
      output +
      ", ground truth: " +
      groundTruth +
      ", lowest loss: " +
      model.lowestLoss
  );
}

async function processDataInBackground(
  dataset: string,
  yColName: string,
  maxRows: number,
  state: stateTypeF
) {
  const payload = {
    dataset: dataset,
    yColName: yColName,
    workerType: WORKER_TYPES.trainDataProcessor,
    maxRows: maxRows,
  };
  const worker = getWorker();

  let returnedData: dataSplit;
  let resolves = {};

  let workerStatus = FAILED_STATUS;
  worker.onmessage = function (message) {
    if (message.data?.status === SUCCESS_STATUS) {
      returnedData = message.data.trainData;
      resolves[0]();
      delete resolves[0];
      workerStatus = message.data.status;
    } else {
      handleError(state, message.data?.error, WORKER_TYPES.trainDataProcessor);
      resolves[0]();
      delete resolves[0];
    }
  };

  await new Promise(function (resolve, reject) {
    // save callbacks for later
    resolves[0] = resolve;
    worker.postMessage(payload);
  });
  if (workerStatus !== SUCCESS_STATUS) {
    return;
  }
  return returnedData;
}

// Some models do not automatically train in a background thread.
async function trainInBackground(
  trainData: dataSplit,
  modelName: string,
  dataInfo: infoType,
  state: stateTypeF
) {
  const payload = {
    trainData: trainData,
    modelName: modelName,
    dataInfo: dataInfo,
    workerType: WORKER_TYPES.trainer,
  };
  const worker = getWorker();

  let returnedData: returnedModelType;
  let resolves = {};
  let rejects = {};

  state.setModelState(
    modelName,
    MODEL_STATES.training,
    state.saveState.modelTraining.modelStates
  );
  let trainStatusLastUpdated = 0;

  worker.onmessage = function (message) {
    if (message.data) {
      let trainState: string = message.data.trainState;
      if (message.data.status === FAILED_STATUS) {
        handleError(state, message.data?.error, WORKER_TYPES.trainer);
        resolves[0]();
        delete resolves[0];
        trainState = "canceled";
      }
      if (state.getTrainingCanceled()) {
        trainState = "canceled";
      }
      if (trainState === "training") {
        const progress: number = message.data.progress;
        trainStatusLastUpdated = BaseModel.setModelProgress(
          progress * 100,
          state,
          modelName,
          trainStatusLastUpdated
        );
      } else if (trainState === "trained") {
        returnedData = message.data;
        state.setModelState(
          modelName,
          MODEL_STATES.trained,
          state.saveState.modelTraining.modelStates
        );
        resolves[0]();
        delete resolves[0];
      } else if (trainState === "canceled") {
        state.setModelState(
          modelName,
          MODEL_STATES.ready,
          state.saveState.modelTraining.modelStates
        );
        state.setTrainingCanceled(true);
        this.terminate();
        if (resolves[0] !== undefined) {
          resolves[0]();
          delete resolves[0];
        }
      }
    }
  };
  await new Promise(function (resolve, reject) {
    // save callbacks for later
    resolves[0] = resolve;
    rejects[0] = reject;
    worker.postMessage(payload);
  });

  return returnedData;
}
async function trainNeuralNet(trainData: dataSplit, state: stateTypeF) {
  if (WEIGHTED && trainData.info.outputType === DATA_TYPES.categorical) {
    trainData.info.weights = getLossWeights(trainData.yTrain);
  }
  const neuralNet = new NeuralNet(trainData.info);
  await neuralNet.train(trainData, state);

  modelLowestLosses[neuralNet.modelName] = neuralNet.lowestLoss;
  await sleep(100);
  return neuralNet;
}
async function trainLogisticRegression(
  trainData: dataSplit,
  state: stateTypeF
) {
  // Support Vector Machine
  let trainedModelLogistic = await trainInBackground(
    trainData,
    CLASSIFICATION_MODELS.logistic,
    trainData.info,
    state
  );
  if (trainedModelLogistic === undefined) {
    return;
  }
  const svm = new LogisticRegression(trainData.info);
  svm.model = SVM.load(trainedModelLogistic.model);
  svm.lowestLoss = trainedModelLogistic.lowestLoss;
  svm.score = trainedModelLogistic.score;
  modelLowestLosses[svm.modelName] = svm.lowestLoss;
  await sleep(100);
  return svm;
}
async function trainLinearRegression(trainData: dataSplit, state: stateTypeF) {
  let trainedModelLinReg = await trainInBackground(
    trainData,
    REGRESSSION_MODELS.linReg,
    trainData.info,
    state
  );
  if (trainedModelLinReg === undefined) {
    return;
  }
  let model: linearModelType = {
    coeff: matrix(trainedModelLinReg.model.coeff["_data"]) as Matrix,
    intercept: trainedModelLinReg.model.intercept as MathType,
  };
  const linReg = new LinearRegression(trainData.info);
  linReg.model = model;
  linReg.lowestLoss = trainedModelLinReg.lowestLoss;
  modelLowestLosses[linReg.modelName] = linReg.lowestLoss;
  await sleep(100);
  return linReg;
}
async function trainRidgeRegression(trainData: dataSplit, state: stateTypeF) {
  let trainedModelRidgeReg = await trainInBackground(
    trainData,
    REGRESSSION_MODELS.ridgeReg,
    trainData.info,
    state
  );
  if (trainedModelRidgeReg === undefined) {
    return;
  }
  let model: linearModelType = {
    coeff: matrix(trainedModelRidgeReg.model.coeff["_data"]) as Matrix,
    intercept: trainedModelRidgeReg.model.intercept as MathType,
  };
  const ridgeReg = new RidgeRegression(trainData.info);
  ridgeReg.model = model;
  ridgeReg.lowestLoss = trainedModelRidgeReg.lowestLoss;
  modelLowestLosses[ridgeReg.modelName] = ridgeReg.lowestLoss;
  await sleep(100);
  return ridgeReg;
}
async function trainRandomForest(trainData: dataSplit, state: stateTypeF) {
  // Random Forest
  let trainedModelRF = await trainInBackground(
    trainData,
    REGRESSSION_MODELS.randomForest,
    trainData.info,
    state
  );
  if (trainedModelRF === undefined) {
    return;
  }
  const randomForest = new RandomForest(trainData.info);
  let modelType;
  if (randomForest.mlDataInfo.outputType === DATA_TYPES.number) {
    modelType = RandomForestRegression;
  } else if (randomForest.mlDataInfo.outputType === DATA_TYPES.categorical) {
    modelType = RandomForestClassifier;
  }
  randomForest.model = modelType.load(JSON.parse(trainedModelRF.model));
  randomForest.lowestLoss = trainedModelRF.lowestLoss;
  modelLowestLosses[randomForest.modelName] = randomForest.lowestLoss;
  await sleep(100);
  return randomForest;
}
async function trainSVM(trainData: dataSplit, state: stateTypeF) {
  // Support Vector Machine
  let trainedModelSVM = await trainInBackground(
    trainData,
    REGRESSSION_MODELS.svm,
    trainData.info,
    state
  );
  if (trainedModelSVM === undefined) {
    return;
  }
  const svm = new SupportVectorMachine(trainData.info);
  svm.model = SVM.load(trainedModelSVM.model);
  svm.lowestLoss = trainedModelSVM.lowestLoss;
  modelLowestLosses[svm.modelName] = svm.lowestLoss;
  await sleep(100);
  return svm;
}
async function trainKNN(trainData: dataSplit, state: stateTypeF) {
  //K-Nearest Neighbours
  const knn = new kNearestNeighbours(trainData.info);
  knn.train(trainData, state);

  modelLowestLosses[knn.modelName] = knn.lowestLoss;
  await sleep(100);
  return knn;
}
async function trainBayes(trainData: dataSplit, state: stateTypeF) {
  //Naive Bayes
  const nb = new NaiveBayes(trainData.info);
  await nb.train(trainData, state, () => 1);
  modelLowestLosses[nb.modelName] = nb.lowestLoss;
  await sleep(100);
  return nb;
}
function chooseModels() {
  let list = [];
  for (const [key, value] of Object.entries(modelLowestLosses)) {
    list.push({ name: key, loss: value });
  }
  list = list.sort((a, b) => a.loss - b.loss);

  let chosenModels: string[] = [list[0].name];
  const lowestLoss = list[0].loss;
  for (let i = 1; i < list.length; i++) {
    if (list[i].loss < lowestLoss * (1 + maxLossDiff)) {
      chosenModels.push(list[i].name);
    }
  }
  return chosenModels;
}
function toTrain(state: stateTypeF, modelName: string) {
  let toTrainAr: boolean[] = [];
  const modelState = state.saveState.modelTraining.modelStates[modelName];
  let col = state.saveState.dataset.getColumn(
    state.saveState.modelTraining.valueToPredict
  );
  toTrainAr.push(state.getCurrentModels(col.columnType).includes(modelName));
  toTrainAr.push(
    modelState != MODEL_STATES.unselected && modelState != MODEL_STATES.premOnly
  );
  toTrainAr.push(!state.getTrainingCanceled());
  return toTrainAr.filter((tf) => tf).length === toTrainAr.length;
}
